import React from 'react'
import { usersApi } from '../api/users'
import { chatsApi } from '../api/chats'
import { useChatStore } from '../stores/chatStore'

// Intercept t.me/ links to open them within the app
async function openTmeLink(url: string) {
  try {
    const m = url.match(/t\.me\/([^/?#]+)/)
    if (!m) { window.open(url, '_blank'); return }
    const username = m[1]
    if (username.startsWith('+')) {
      // invite link — just open externally
      window.open(url, '_blank')
      return
    }
    const users = await usersApi.search(username)
    const user = (users.users ?? []).find((u) => u.username === username)
    if (!user) { window.open(url, '_blank'); return }
    const chat = await chatsApi.getOrCreate(user.id)
    useChatStore.getState().upsertChat({ ...chat, peer: user })
    useChatStore.getState().setActiveChat(chat.id)
  } catch {
    window.open(url, '_blank')
  }
}

function linkifyText(text: string, startKey: number): { nodes: React.ReactNode[]; key: number } {
  // Combined: URLs + @mentions + #hashtags
  const combined = /https?:\/\/[^\s<>"']+|@\w+|#\w+/g
  const parts: React.ReactNode[] = []
  let k = startKey
  let lastIdx = 0
  let m: RegExpExecArray | null
  combined.lastIndex = 0
  while ((m = combined.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index))
    const token = m[0]
    if (token.startsWith('http')) {
      const isTme = /^https?:\/\/t\.me\//i.test(token)
      if (isTme) {
        parts.push(
          <button key={k++}
            onClick={(e) => { e.stopPropagation(); openTmeLink(token) }}
            className="text-txt-link underline break-all hover:opacity-80 transition-opacity">
            {token}
          </button>
        )
      } else {
      parts.push(
        <a key={k++} href={token} target="_blank" rel="noopener noreferrer"
           className="text-txt-link underline break-all hover:opacity-80 transition-opacity"
           onClick={e => e.stopPropagation()}>
          {token}
        </a>
      )
      }
    } else if (token.startsWith('@')) {
      parts.push(
        <span key={k++} className="text-accent font-medium cursor-pointer hover:underline">
          {token}
        </span>
      )
    } else if (token.startsWith('#')) {
      parts.push(
        <span key={k++} className="text-accent cursor-pointer hover:underline">
          {token}
        </span>
      )
    }
    lastIdx = m.index + token.length
  }
  if (lastIdx < text.length) parts.push(text.slice(lastIdx))
  return { nodes: parts.length ? parts : [text], key: k }
}


function Spoiler({ children }: { children: React.ReactNode }) {
  const [revealed, setRevealed] = React.useState(false)
  return (
    <span
      onClick={() => setRevealed((v) => !v)}
      className={`cursor-pointer rounded px-0.5 select-none transition-all
        ${revealed ? '' : 'bg-txt-primary/80 text-transparent hover:bg-txt-primary/60'}`}
    >
      {children}
    </span>
  )
}

export function parseFormatting(text: string): React.ReactNode[] {
  // Combined regex for: ```pre```, `code`, **bold**, __underline__, _italic_, ~~strike~~, ||spoiler||
  const combined = /```([\s\S]*?)```|`([^`]+)`|\*\*([^*]+)\*\*|__([^_]+)__|_([^_]+)_|~~([^~]+)~~|\|\|([^|]+)\|\|/g
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let key = 0
  let match: RegExpExecArray | null

  while ((match = combined.exec(text)) !== null) {
    // Add plain text before this match (linkified)
    if (match.index > lastIndex) {
      const { nodes, key: nextKey } = linkifyText(text.slice(lastIndex, match.index), key)
      parts.push(...nodes)
      key = nextKey
    }

    if (match[1] !== undefined) {
      // ```pre```
      parts.push(
        <code key={key++} className="block bg-black/20 rounded px-2 py-1 font-mono text-sm whitespace-pre-wrap">
          {match[1]}
        </code>,
      )
    } else if (match[2] !== undefined) {
      // `code`
      parts.push(
        <code key={key++} className="bg-black/20 rounded px-1 font-mono text-[13px]">
          {match[2]}
        </code>,
      )
    } else if (match[3] !== undefined) {
      // **bold**
      parts.push(<strong key={key++}>{match[3]}</strong>)
    } else if (match[4] !== undefined) {
      // __underline__
      parts.push(<u key={key++}>{match[4]}</u>)
    } else if (match[5] !== undefined) {
      // _italic_
      parts.push(<em key={key++}>{match[5]}</em>)
    } else if (match[6] !== undefined) {
      // ~~strikethrough~~
      parts.push(<s key={key++}>{match[6]}</s>)
    } else if (match[7] !== undefined) {
      // ||spoiler||
      parts.push(<Spoiler key={key++}>{match[7]}</Spoiler>)
    }

    lastIndex = match.index + match[0].length
  }

  // Remaining plain text (linkified)
  if (lastIndex < text.length) {
    const { nodes, key: nextKey } = linkifyText(text.slice(lastIndex), key)
    parts.push(...nodes)
    key = nextKey
  }

  return parts.length ? parts : [text]
}
